import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Status is always a badge with a label. Colour never carries the meaning alone.
 * `unknown` is dashed on purpose: an unproven outcome must never look like a result.
 */
const badgeVariants = cva(
  "inline-flex h-[1.375rem] items-center gap-1.5 whitespace-nowrap rounded-full border px-2 text-xs font-medium before:size-1.5 before:shrink-0 before:rounded-full before:bg-current before:content-['']",
  {
    variants: {
      variant: {
        outline: "border-border text-foreground",
        secondary: "border-transparent bg-secondary text-secondary-foreground",
        success: "border-success/35 bg-success/6 text-success hover:bg-success/6",
        warning: "border-warning/40 bg-warning/6 text-warning hover:bg-warning/6",
        danger: "border-danger/35 bg-danger/6 text-danger hover:bg-danger/6",
        destructive:
          "border-destructive/35 bg-destructive/6 text-destructive-text hover:bg-destructive/6",
        info: "border-info/35 bg-info/6 text-info hover:bg-info/6",
        muted: "border-border bg-muted text-muted-foreground",
        unknown: "border-dashed border-unknown/60 bg-unknown/6 text-unknown hover:bg-unknown/6",
        lime: "border-lime/30 bg-lime/10 text-chart font-bold hover:bg-lime/15",
      },
    },
    defaultVariants: { variant: "outline" },
  },
);

export interface BadgeProps
  extends React.ComponentProps<"span">, VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { Badge, badgeVariants };
